import React, { memo, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { SwipeAnimation } from 'src/react/_Layout/SwipeBack.styles';

const SWIPE_TRIGGER_DISTANCE = 150;

type SwipeBackProps = {
  handler?: () => void;
};

const SwipeBack = ({ handler }: SwipeBackProps) => {
  const history = useHistory();
  const swipeAnimation = useRef<HTMLDivElement>(null);
  const handlerRef = useRef<() => void>(() => history.goBack());

  useEffect(() => {
    handlerRef.current = handler ?? (() => history.goBack());
  });

  useEffect(() => {
    const node = swipeAnimation.current;
    const target = node?.parentElement;
    if (!node || !target) return;

    let xDown: number | null = null;
    let yDown: number | null = null;
    let swipeDistance = 0;

    const reset = () => {
      xDown = null;
      yDown = null;
      swipeDistance = 0;
      node.style.width = '0';
      node.style.left = '0';
      node.style.opacity = '0';
    };

    const handleTouchStart = (evt: TouchEvent) => {
      xDown = evt.touches[0].clientX;
      yDown = evt.touches[0].clientY;
      swipeDistance = 0;
    };

    const handleTouchMove = (evt: TouchEvent) => {
      if (xDown === null || yDown === null) return;

      const xDiff = xDown - evt.touches[0].clientX;
      const yDiff = yDown - evt.touches[0].clientY;
      if (Math.abs(xDiff) <= Math.abs(yDiff)) return;

      const value = Math.trunc(xDiff);
      if (value < 1) {
        if (-value <= SWIPE_TRIGGER_DISTANCE) {
          node.style.width = `${-value}px`;
          node.style.left = `${value / 2}px`;
          node.style.opacity = String(-value / 2 / 200);
        }
        swipeDistance = -value;
      }
    };

    const handleTouchEnd = () => {
      const shouldGoBack = swipeDistance >= SWIPE_TRIGGER_DISTANCE;
      reset();
      if (shouldGoBack) handlerRef.current();
    };

    target.addEventListener('touchstart', handleTouchStart);
    target.addEventListener('touchmove', handleTouchMove);
    target.addEventListener('touchend', handleTouchEnd);
    target.addEventListener('touchcancel', reset);
    return () => {
      target.removeEventListener('touchstart', handleTouchStart);
      target.removeEventListener('touchmove', handleTouchMove);
      target.removeEventListener('touchend', handleTouchEnd);
      target.removeEventListener('touchcancel', reset);
    };
  }, []);

  return <SwipeAnimation ref={swipeAnimation} />;
};

export default memo(SwipeBack);
